import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminSeasonsAPI } from '../services/adminApi';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 2rem;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: 1.1rem;
`;

const Content = styled.div`
  padding: 2rem;
`;

const CreateButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background: #2563eb;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const FormContainer = styled.div`
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb;
`;

const FormTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #374151;
  font-size: 1.25rem;
  font-weight: 600;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const SeasonsList = styled.div`
  display: grid;
  gap: 1rem;
`;

const SeasonCard = styled.div`
  padding: 1.25rem;
  padding-right: 5rem; /* Make space for action buttons */
  background: ${props => props.selected ? '#eff6ff' : 'white'};
  border-radius: 8px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const SeasonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SeasonName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 1.1rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
  background-color: ${props => props.color || '#6b7280'};
`;

const SeasonDates = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const Message = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  background-color: ${props => props.type === 'success' ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.type === 'success' ? '#065f46' : '#991b1b'};
  border: 1px solid ${props => props.type === 'success' ? '#a7f3d0' : '#fecaca'};
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const ActionButtons = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  gap: 0.5rem;
  z-index: 10;
`;

const SeasonActionButton = styled.button`
  background: ${props => props.variant === 'edit' ? '#3b82f6' : '#ef4444'};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  opacity: 1;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    background: ${props => props.variant === 'edit' ? '#2563eb' : '#dc2626'};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #6b7280;
`;

const AdminSeasonsManagement = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [newSeason, setNewSeason] = useState({
    name: '',
    start_date: '',
    end_date: '',
    registration_start: '',
    registration_end: ''
  });
  const [editSeason, setEditSeason] = useState({
    name: '',
    start_date: '',
    end_date: '',
    registration_start: '',
    registration_end: '',
    status: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const queryClient = useQueryClient();

  // Fetch seasons
  const { data: seasonsData, isLoading, error } = useQuery(
    'seasons',
    () => adminSeasonsAPI.getAll(),
    {
      retry: false,
      onSuccess: (data) => {
        if (data.success && data.data.length > 0 && !selectedSeason) {
          setSelectedSeason(data.data[0].id);
        }
      },
      onError: (error) => {
        console.error('Error fetching seasons:', error);
      }
    }
  );

  // Create season mutation
  const createSeasonMutation = useMutation(
    (seasonData) => adminSeasonsAPI.create(seasonData),
    {
      onSuccess: (data) => {
        setMessage({ type: 'success', text: 'Phase created successfully!' });
        setShowCreateForm(false);
        setNewSeason({
          name: '',
          start_date: '',
          end_date: '',
          registration_start: '',
          registration_end: ''
        });
        queryClient.invalidateQueries('seasons');
      },
      onError: (error) => {
        setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to create phase' });
      }
    }
  );

  // Update season mutation
  const updateSeasonMutation = useMutation(
    ({ seasonId, seasonData }) => adminSeasonsAPI.update(seasonId, seasonData),
    {
      onSuccess: (data) => {
        setMessage({ type: 'success', text: 'Phase updated successfully!' });
        setShowEditForm(false);
        setEditingSeason(null);
        setEditSeason({
          name: '',
          start_date: '',
          end_date: '',
          registration_start: '',
          registration_end: '',
          status: ''
        });
        queryClient.invalidateQueries('seasons');
      },
      onError: (error) => {
        setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update phase' });
      }
    }
  );

  // Delete season mutation
  const deleteSeasonMutation = useMutation(
    (seasonId) => adminSeasonsAPI.delete(seasonId),
    {
      onSuccess: (data) => {
        setMessage({ type: 'success', text: 'Phase deleted successfully!' });
        if (selectedSeason === data.data.id) {
          setSelectedSeason('');
        }
        queryClient.invalidateQueries('seasons');
      },
      onError: (error) => {
        setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete phase' });
      }
    }
  );

  const validateSeasonDates = (s) => {
    if (!s.start_date || !s.end_date) return null;
    if (s.end_date < s.start_date) {
      return 'Phase end date must be on or after phase start.';
    }
    if (s.registration_start && s.registration_end && s.registration_end < s.registration_start) {
      return 'Registration end must be on or after registration start.';
    }
    return null;
  };

  const handleCreateSeason = () => {
    if (!newSeason.name || !newSeason.start_date || !newSeason.end_date) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }
    const err = validateSeasonDates(newSeason);
    if (err) {
      setMessage({ type: 'error', text: err });
      return;
    }
    createSeasonMutation.mutate(newSeason);
  };

  const handleSeasonSelect = (seasonId) => {
    setSelectedSeason(seasonId);
    setMessage({ type: '', text: '' });
  };

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    
    // If it's already a string in YYYY-MM-DD format, return it
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // If it's a Date object or ISO string, extract YYYY-MM-DD
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return '';
    }
  };

  const handleEditSeason = (season) => {
    setEditingSeason(season);
    setEditSeason({
      name: season.name,
      start_date: formatDateForInput(season.start_date),
      end_date: formatDateForInput(season.end_date),
      registration_start: formatDateForInput(season.registration_start),
      registration_end: formatDateForInput(season.registration_end),
      status: season.status || ''
    });
    setShowEditForm(true);
    setMessage({ type: '', text: '' });
  };

  const handleUpdateSeason = () => {
    if (!editSeason.name || !editSeason.start_date || !editSeason.end_date) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }
    const err = validateSeasonDates(editSeason);
    if (err) {
      setMessage({ type: 'error', text: err });
      return;
    }

    // Don't send status field to preserve existing status
    const { status, ...seasonDataWithoutStatus } = editSeason;

    updateSeasonMutation.mutate({
      seasonId: editingSeason.id,
      seasonData: seasonDataWithoutStatus
    });
  };

  const handleDeleteSeason = (seasonId, seasonName) => {
    if (window.confirm(`Are you sure you want to delete the phase "${seasonName}"? This action cannot be undone and will also delete all associated leagues, games, and registrations.`)) {
      deleteSeasonMutation.mutate(seasonId);
    }
  };

  const getSeasonStatus = (season) => {
    const SWISS_TIMEZONE = 'Europe/Zurich';
    const toYMD = (v) => {
      if (!v) return '';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-CA', { timeZone: SWISS_TIMEZONE });
    };
    const nowSwiss = toYMD(new Date());
    const startDate = toYMD(season.start_date);
    const endDate = toYMD(season.end_date);
    const regStart = toYMD(season.registration_start);
    const regEnd = toYMD(season.registration_end);

    if (!regStart || !regEnd || !startDate || !endDate) return 'unknown';

    if (nowSwiss > endDate) return 'completed';

    if (nowSwiss < regStart) return 'upcoming';
    if (nowSwiss >= regStart && nowSwiss <= regEnd) return 'registration';
    if (nowSwiss > regEnd && nowSwiss < startDate) return 'preparation';
    if (nowSwiss >= startDate && nowSwiss <= endDate) return 'active';
    return 'unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return '#6b7280';
      case 'registration': return '#3b82f6';
      case 'preparation': return '#f59e0b';
      case 'active': return '#10b981';
      case 'completed': return '#6b7280';
      default: return '#9ca3af';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'upcoming': return 'Upcoming';
      case 'registration': return 'Registration Open';
      case 'preparation': return 'Preparation';
      case 'active': return 'Active';
      case 'completed': return 'Phase ended';
      default: return 'Unknown';
    }
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner>Loading phases...</LoadingSpinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <Message type="error">Error loading phases. Please try again.</Message>
        </Content>
      </Container>
    );
  }

  const seasons = seasonsData?.data || [];

  return (
    <Container>
      <Header>
        <Title>Phase Management</Title>
        <Subtitle>Create and manage league phases</Subtitle>
      </Header>

      <Content>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ margin: 0, color: '#1f2937' }}>Phases</h2>
          <CreateButton onClick={() => setShowCreateForm(true)}>
            Create New Phase
          </CreateButton>
        </div>
        
        {/* Message Display */}
        {message.text && (
          <Message type={message.type}>
            {message.text}
          </Message>
        )}

        {/* Create Season Form */}
        {showCreateForm && (
          <FormContainer>
            <FormTitle>Create New Phase</FormTitle>
            
            <FormGrid>
              <FormGroup>
                <Label>Phase Name *</Label>
                <Input
                  type="text"
                  value={newSeason.name}
                  onChange={(e) => setNewSeason({...newSeason, name: e.target.value})}
                  placeholder="e.g., Winter 2025 Phase"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Phase Start Date *</Label>
                <Input
                  type="date"
                  value={newSeason.start_date}
                  onChange={(e) => setNewSeason({...newSeason, start_date: e.target.value})}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Phase End Date *</Label>
                <Input
                  type="date"
                  value={newSeason.end_date}
                  onChange={(e) => setNewSeason({...newSeason, end_date: e.target.value})}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Registration Start Date</Label>
                <Input
                  type="date"
                  value={newSeason.registration_start}
                  onChange={(e) => setNewSeason({...newSeason, registration_start: e.target.value})}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Registration End Date</Label>
                <Input
                  type="date"
                  value={newSeason.registration_end}
                  onChange={(e) => setNewSeason({...newSeason, registration_end: e.target.value})}
                />
              </FormGroup>
            </FormGrid>
            
            <ButtonGroup>
              <CreateButton
                onClick={handleCreateSeason}
                disabled={createSeasonMutation.isLoading}
              >
                {createSeasonMutation.isLoading ? 'Creating...' : 'Create Phase'}
              </CreateButton>
              <CancelButton onClick={() => setShowCreateForm(false)}>
                Cancel
              </CancelButton>
            </ButtonGroup>
          </FormContainer>
        )}

        {/* Edit Season Form */}
        {showEditForm && editingSeason && (
          <FormContainer>
            <FormTitle>Edit Phase: {editingSeason.name}</FormTitle>
            
            <FormGrid>
              <FormGroup>
                <Label>Phase Name *</Label>
                <Input
                  type="text"
                  value={editSeason.name}
                  onChange={(e) => setEditSeason({...editSeason, name: e.target.value})}
                  placeholder="e.g., Winter 2025 Phase"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Phase Start Date *</Label>
                <Input
                  type="date"
                  value={editSeason.start_date}
                  onChange={(e) => setEditSeason({...editSeason, start_date: e.target.value})}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Phase End Date *</Label>
                <Input
                  type="date"
                  value={editSeason.end_date}
                  onChange={(e) => setEditSeason({...editSeason, end_date: e.target.value})}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Registration Start Date</Label>
                <Input
                  type="date"
                  value={editSeason.registration_start}
                  onChange={(e) => setEditSeason({...editSeason, registration_start: e.target.value})}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Registration End Date</Label>
                <Input
                  type="date"
                  value={editSeason.registration_end}
                  onChange={(e) => setEditSeason({...editSeason, registration_end: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label>Status</Label>
                <Input
                  as="select"
                  value={editSeason.status}
                  onChange={(e) => setEditSeason({...editSeason, status: e.target.value})}
                >
                  <option value="">Auto-detect</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="registration">Registration Open</option>
                  <option value="preparation">Preparation</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </Input>
              </FormGroup>
            </FormGrid>
            
            <ButtonGroup>
              <CreateButton
                onClick={handleUpdateSeason}
                disabled={updateSeasonMutation.isLoading}
              >
                {updateSeasonMutation.isLoading ? 'Updating...' : 'Update Phase'}
              </CreateButton>
              <CancelButton onClick={() => {
                setShowEditForm(false);
                setEditingSeason(null);
              }}>
                Cancel
              </CancelButton>
            </ButtonGroup>
          </FormContainer>
        )}

        {/* Seasons List */}
        <div>
          <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Available Phases</h3>
          
          {seasons.length === 0 ? (
            <EmptyState>
              No phases available. Create your first phase to get started.
            </EmptyState>
          ) : (
            <SeasonsList>
              {seasons.map(season => {
                const status = getSeasonStatus(season);
                const isSelected = selectedSeason === season.id;
                
                return (
                  <SeasonCard 
                    key={season.id} 
                    selected={isSelected}
                    onClick={() => handleSeasonSelect(season.id)}
                  >
                    <ActionButtons>
                      <SeasonActionButton
                        variant="edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSeason(season);
                        }}
                        disabled={updateSeasonMutation.isLoading}
                        title="Edit Phase"
                      >
                        {updateSeasonMutation.isLoading ? '...' : '✏️'}
                      </SeasonActionButton>
                      <SeasonActionButton
                        variant="delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSeason(season.id, season.name);
                        }}
                        disabled={deleteSeasonMutation.isLoading}
                        title="Delete Phase"
                      >
                        {deleteSeasonMutation.isLoading ? '...' : '🗑️'}
                      </SeasonActionButton>
                    </ActionButtons>
                    
                    <SeasonHeader>
                      <SeasonName>
                        {season.name}
                        {isSelected && <span style={{ marginLeft: '0.5rem', color: '#3b82f6' }}>← Selected</span>}
                      </SeasonName>
                      <StatusBadge color={getStatusColor(status)}>
                        {getStatusLabel(status)}
                      </StatusBadge>
                    </SeasonHeader>
                    <SeasonDates>
                      {new Date(season.start_date).toLocaleDateString()} - {new Date(season.end_date).toLocaleDateString()}
                    </SeasonDates>
                  </SeasonCard>
                );
              })}
            </SeasonsList>
          )}
        </div>
      </Content>
    </Container>
  );
};

export default AdminSeasonsManagement;
